import { useCallback, useEffect, useState } from "react";

/** How often a visible tab re-checks. A deploy is rare; the file is ~30 bytes. */
const POLL_MS = 120_000;

/**
 * Notices when the server is serving a newer build than this tab is running.
 *
 * The signal is `version.json`, a fingerprint of the emitted bundle written at
 * build time (see the `emit-version` plugin in vite.config). The tab does not
 * need to be told its own version: the first successful fetch *is* its version,
 * because that is the build it loaded from. Every later fetch is compared with
 * that baseline.
 *
 * Nothing reloads on its own. Someone may be halfway through an invoice or a
 * call note, and throwing that away to install a deploy nobody asked them about
 * is worse than running yesterday's code for another ten minutes.
 *
 * Polling only runs while the tab is visible, and a tab coming back to the
 * foreground checks immediately — the realistic case is a laptop opened after
 * lunch, not someone staring at the screen when a deploy lands.
 */
export function useAppUpdate(): { updateReady: boolean; dismiss: () => void } {
  // All three are state, not refs: `updateReady` is derived from them during
  // render, and a ref read there is a value React never promised was current.
  const [baseline, setBaseline] = useState<string | null>(null);
  const [serverBuild, setServerBuild] = useState<string | null>(null);
  const [dismissedBuild, setDismissedBuild] = useState<string | null>(null);

  const check = useCallback(async () => {
    try {
      // no-store rather than a cache-busting query: the server already says
      // no-store, and a query string would defeat any proxy in front of it.
      const res = await fetch("/version.json", { cache: "no-store" });
      if (!res.ok) return;
      const body: unknown = await res.json();
      const build = (body as { build?: unknown } | null)?.build;
      if (typeof build !== "string" || build === "") return;
      // First answer wins and is never revised — it is the build this tab
      // loaded from, whatever the server goes on to serve.
      setBaseline((first) => first ?? build);
      setServerBuild(build);
    } catch {
      // Offline, mid-deploy, or a stray HTML response. Silence is right: this
      // is a background nicety and must never surface as an error.
    }
  }, []);

  useEffect(() => {
    // Vite serves no version.json in dev, and a dev tab reloads itself anyway.
    if (import.meta.env.DEV) return;

    // check() is async and touches state only after its first await, which is
    // the callback case this rule exists to allow — it cannot tell the two apart.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void check();
    const timer = setInterval(() => {
      if (document.visibilityState === "visible") void check();
    }, POLL_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible") void check();
    };
    document.addEventListener("visibilitychange", onVisible);

    // Proof rather than suspicion: a lazy route chunk that 404s means the
    // bundle this tab was built against is gone from the server. Routes are
    // code-split, so this is how a stale tab usually finds out — by breaking.
    const onPreloadError = () => setServerBuild("stale");
    window.addEventListener("vite:preloadError", onPreloadError);

    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("vite:preloadError", onPreloadError);
    };
  }, [check]);

  const changed = serverBuild !== null && serverBuild !== baseline;
  // Dismissal is remembered per build, so "later" holds until the next deploy
  // rather than for two minutes.
  const dismiss = useCallback(() => setDismissedBuild(serverBuild), [serverBuild]);

  return { updateReady: changed && serverBuild !== dismissedBuild, dismiss };
}
